export const HOSTED_CHECKOUT_EXPERIMENT_NAME = 'stripe_hosted_checkout_2026_08';
export const HOSTED_CHECKOUT_FEATURE_FLAG = 'stripe-hosted-checkout-2026-08';

export type HostedCheckoutItem = {
  productSlug: string;
  planKey?: string | null;
  quantity?: number;
};

export type HostedCheckoutAttribution = {
  sourceRoute: string;
  flowContext?: string | null;
  experimentKey: typeof HOSTED_CHECKOUT_FEATURE_FLAG;
  variant: 'stripe_hosted';
  assignmentId: string;
  subjectId: string;
  surface: string;
  exposedAt: string;
};

type CreateHostedCheckoutSessionInput = {
  apiUrl: string;
  token?: string | null;
  checkoutAttemptId: string;
  items: HostedCheckoutItem[];
  promoCode?: string | null;
  returnContext: 'subscriptions' | 'signup_survey' | 'ai_agent';
  attribution: HostedCheckoutAttribution;
};

type NormalizedHostedCheckoutItem = {
  productSlug: string;
  planKey?: string;
  quantity: number;
};

type HostedCheckoutRequestBody = {
  checkoutAttemptId: string;
  items: NormalizedHostedCheckoutItem[];
  promoCode?: string;
  returnContext: CreateHostedCheckoutSessionInput['returnContext'];
  attribution: HostedCheckoutAttribution;
};

type HostedCheckoutRequestHeaders = {
  Authorization?: string;
  'Content-Type': string;
  'X-Request-Id': string;
};

type HostedCheckoutReturnBody = {
  attemptId: string;
  returnToken: string;
  sessionId?: string;
  canceled?: boolean;
};

type HostedCheckoutNavigationInput = {
  redirectTo?: string | null;
  returnPath?: string | null;
  userEmail?: string | null;
  origin: string;
};

const isStripeCheckoutUrl = (value: string) => {
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      (url.hostname === 'checkout.stripe.com' || url.hostname.endsWith('.checkout.stripe.com'))
    );
  } catch {
    return false;
  }
};

export function buildHostedCheckoutNavigation(input: HostedCheckoutNavigationInput) {
  let path = input.returnPath || '/account-settings/subscriptions';
  if (input.redirectTo) {
    try {
      const target = new URL(input.redirectTo, input.origin);
      if (target.origin === input.origin) {
        path = `${target.pathname}${target.search}${target.hash}`;
      }
    } catch {
      // Malformed backend redirects fall back to the server-approved return path.
    }
  }
  return {
    path,
    state: input.userEmail ? { email: input.userEmail } : undefined,
  };
}

export async function createHostedCheckoutSession(input: CreateHostedCheckoutSessionInput) {
  const items = input.items.map((item) => {
    const normalized: NormalizedHostedCheckoutItem = {
      productSlug: item.productSlug,
      quantity: item.quantity ?? 1,
    };
    if (item.planKey) normalized.planKey = item.planKey;
    return normalized;
  });
  const body: HostedCheckoutRequestBody = {
    checkoutAttemptId: input.checkoutAttemptId,
    items,
    returnContext: input.returnContext,
    attribution: { ...input.attribution },
  };
  if (input.promoCode) body.promoCode = input.promoCode;

  const headers: HostedCheckoutRequestHeaders = {
    'Content-Type': 'application/json',
    'X-Request-Id': input.checkoutAttemptId,
  };
  if (input.token) headers.Authorization = `Bearer ${input.token}`;
  const response = await fetch(`${input.apiUrl}/stripe/hosted-checkout-session`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || 'Secure checkout could not be opened. Please try again.');
  }
  const checkoutUrl = String(data.checkoutUrl || '');
  if (!isStripeCheckoutUrl(checkoutUrl)) {
    throw new Error('Server returned an invalid checkout URL.');
  }
  return {
    attemptId: String(data.attemptId || ''),
    sessionId: String(data.sessionId || ''),
    checkoutUrl,
    expiresAt: data.expiresAt ? String(data.expiresAt) : null,
  };
}

export async function completeHostedCheckoutReturn({
  apiUrl,
  attemptId,
  returnToken,
  sessionId,
  canceled,
}: {
  apiUrl: string;
  attemptId: string;
  returnToken: string;
  sessionId?: string | null;
  canceled?: boolean;
}) {
  const body: HostedCheckoutReturnBody = { attemptId, returnToken };
  if (sessionId) body.sessionId = sessionId;
  if (canceled) body.canceled = true;
  const response = await fetch(`${apiUrl}/stripe/hosted-checkout-return`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || 'Checkout return could not be verified.');
  return data;
}
